/**  MODIFIED COPY OF src/common-app/state/modules/engines/voice-mask/actions.ts TODO find way to reuse common code! */
import { TDOId } from '@common-modules/universal/models/Brands';
import {
  createGraphQLFailureAction,
  createGraphQLSuccessAction,
} from '@helpers/callGraphQLApi';
import { createAction } from '@reduxjs/toolkit';
// TaskStatus, not JobStatus, for both replies below: the schema types
// `Job.status` as TaskStatus as well, and the task reply can only be TaskStatus
// (VE-30784). See JobState['status'] in ./store.
import type { TaskStatus } from 'veritone-types';

export const NOOP = 'noop';

/**
 * Begins the process of starting speaker-separation engine job.
 * Includes polling and loading results.
 */
export const PROCESS_ENGINE_REQUEST = createAction<{
  tdoId: TDOId;
  tdoName: string;
  payload: {
    targetId: string;
    url: string;
    transcriptionLanguage?: string;
  };
}>('vtn/redact/engine/speaker-separation/PROCESS_ENGINE_REQUEST');

export const PROCESS_ENGINE_REQUEST_SUCCESS = createAction<{
  jobId: string;
  tdoId: TDOId;
}>('vtn/redact/engine/speaker-separation/PROCESS_ENGINE_REQUEST_SUCCESS');

export const processEngineRequestAction = ({
  tdoId,
  tdoName,
  targetId,
  url,
  transcriptionLanguage,
}: {
  tdoId: TDOId;
  tdoName: string;
  targetId: string;
  url: string;
  /**
   * The language of the transcript this separation belongs to, when the
   * caller knows it better than the store does.
   *
   * The ▾ menu omits it: the transcript on screen IS the one being separated,
   * so reading it back is right there. A replace must send it — by the time
   * the job starts, the transcript the store knows about has been deleted and
   * the one this separation is for does not exist yet.
   */
  transcriptionLanguage?: string;
}) =>
  PROCESS_ENGINE_REQUEST({
    tdoId,
    tdoName,
    payload: { targetId, url, transcriptionLanguage },
  });

export const processEngineRequestSuccessAction = ({
  tdoId,
  jobId,
}: {
  jobId: string;
  tdoId: TDOId;
}) => PROCESS_ENGINE_REQUEST_SUCCESS({ tdoId, jobId });

/**
 * Create engine job actions.
 */
export const CREATE_ENGINE_JOB = createAction<{
  tdoId: TDOId;
  tdoName: string;
  payload: { targetId: string; url: string };
}>('vtn/redact/engine/speaker-separation/CREATE_ENGINE_JOB');

export const CREATE_ENGINE_JOB_SUCCESS = createGraphQLSuccessAction<{
  createJob: {
    id: string;
    targetId: TDOId;
    status: string;
  };
}>('vtn/redact/engine/speaker-separation/CREATE_ENGINE_JOB_SUCCESS');

export const CREATE_ENGINE_JOB_FAILURE = createGraphQLFailureAction<
  any,
  { id: TDOId; tdoName: string },
  true
>('vtn/redact/engine/speaker-separation/CREATE_ENGINE_JOB_FAILURE');

export const createEngineJobAction = ({
  tdoId,
  tdoName,
  payload,
}: {
  tdoId: TDOId;
  tdoName: string;
  payload: { targetId: string; url: string };
}) => CREATE_ENGINE_JOB({ tdoId, tdoName, payload });

/**
 * Check job status actions.
 */
export const CHECK_JOB_STATUS = createAction<{
  jobId: string;
  tdoId: TDOId;
}>('vtn/redact/engine/speaker-separation/CHECK_JOB_STATUS');

export const CHECK_JOB_STATUS_SUCCESS = createGraphQLSuccessAction<{
  job: {
    id: string;
    targetId: TDOId;
    status: TaskStatus;
  };
}>('vtn/redact/engine/speaker-separation/CHECK_JOB_STATUS_SUCCESS');

export const CHECK_JOB_STATUS_FAILURE = createGraphQLFailureAction(
  'vtn/redact/engine/speaker-separation/CHECK_JOB_STATUS_FAILURE'
);

export const checkJobStatusAction = ({
  tdoId,
  jobId,
}: {
  tdoId: TDOId;
  jobId: string;
}) => CHECK_JOB_STATUS({ tdoId, jobId });

/**
 * Check task status actions.
 *
 * Needed for runs started by the upload DAG (VE-30784). Separation's branch
 * there runs under the INGESTION job alongside transcription and detection, so
 * the job's status only turns complete once every other upload engine has
 * finished — far later than separation's own results exist, and on a long
 * video not at all until the whole upload is done. The task carries the answer
 * this poller actually wants. Same reasoning, and the same shape, as
 * transcription and voice mask.
 */
export const CHECK_TASK_STATUS = createAction<{
  jobId: string;
  taskId: string;
  tdoId: TDOId;
}>('vtn/redact/engine/speaker-separation/CHECK_TASK_STATUS');

export const CHECK_TASK_STATUS_SUCCESS = createGraphQLSuccessAction<{
  task: {
    id: string;
    jobId: string;
    targetId: TDOId;
    status: TaskStatus;
  };
}>('vtn/redact/engine/speaker-separation/CHECK_TASK_STATUS_SUCCESS');

export const CHECK_TASK_STATUS_FAILURE = createGraphQLFailureAction(
  'vtn/redact/engine/speaker-separation/CHECK_TASK_STATUS_FAILURE'
);

export const checkTaskStatusAction = ({
  tdoId,
  jobId,
  taskId,
}: {
  tdoId: TDOId;
  jobId: string;
  taskId: string;
}) => CHECK_TASK_STATUS({ tdoId, taskId, jobId });

/**
 * Polling actions.
 *
 * `taskId` is optional: a job this app created is its own, so its status is
 * the run's status, and the poller falls back to it. A run picked up from the
 * upload DAG must pass one — see CHECK_TASK_STATUS.
 */
export const START_POLLING_ENGINE_RESULTS = createAction<{
  tdoId: TDOId;
  jobId: string;
  taskId?: string;
}>('vtn/redact/engine/speaker-separation/START_POLLING_ENGINE_RESULTS');

export const STOP_POLLING_ENGINE_RESULTS = createAction<{
  tdoId: TDOId;
  jobId: string;
}>('vtn/redact/engine/speaker-separation/STOP_POLLING_ENGINE_RESULTS');

export const startPollingEngineResultsAction = ({
  tdoId,
  jobId,
  taskId,
}: {
  tdoId: TDOId;
  jobId: string;
  taskId?: string | undefined;
}) => START_POLLING_ENGINE_RESULTS({ tdoId, jobId, taskId });

export const stopPollingEngineResultsAction = ({
  tdoId,
  jobId,
}: {
  tdoId: TDOId;
  jobId: string;
}) => STOP_POLLING_ENGINE_RESULTS({ tdoId, jobId });
